import React, { useState, useEffect } from 'react';
import { API_BASE_URL, CONNECT_UI_URL, CONNECT_CLIENT_ID } from '../../config.js';

const UsersManagement = ({ fetchWithAuth }) => {
  const [users, setUsers] = useState([]);
  const [cockpits, setCockpits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch users and cockpits data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch users
        const usersResponse = await fetchWithAuth(`${API_BASE_URL}/api/admin/users`);
        if (!usersResponse.ok) throw new Error('Failed to fetch users');
        const usersData = await usersResponse.json();

        // Fetch cockpits
        const cockpitsResponse = await fetchWithAuth(`${API_BASE_URL}/api/admin/cockpits`);
        if (!cockpitsResponse.ok) throw new Error('Failed to fetch cockpits');
        const cockpitsData = await cockpitsResponse.json();

        setUsers(usersData);
        setCockpits(cockpitsData);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchWithAuth]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
      <div className="p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Gestion des utilisateurs</h2>

        <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
            Les utilisateurs et les droits se gèrent dans Globe Connect.
          </p>
          <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
            La liste ci-dessous est floutée volontairement : Cockpit peut être en retard sur Connect,
            seule source de vérité des rôles et des accès.
          </p>
          <a
            href={`${CONNECT_UI_URL}/admin/droits?app=${CONNECT_CLIENT_ID}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 border border-blue-300 dark:border-blue-700 rounded-md shadow-sm text-sm font-medium text-blue-700 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
          >
            Gérer les utilisateurs dans Connect
          </a>
        </div>

        {/* Blurred on purpose: the list stays as a hint that data exists, unreadable so nobody
            deduces a role here. Inert and aria-hidden — a filter or a sort would hand back
            exactly what the blur removes. `grayscale` with it: a blur keeps the badge colours, and
            the green of Super Admin names the role on its own. */}
        <div className="overflow-x-auto blur-[7px] grayscale select-none pointer-events-none" aria-hidden="true">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {["Nom d'utilisateur", 'Client', 'Cockpits', 'Autorisation'].map(label => (
                  <th
                    key={label}
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {users.map(user => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {user.username}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                    {user.client || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-300">
                    {user.issuperadmin || user.cockpits?.allowed?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {user.issuperadmin ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            Tous les cockpits
                          </span>
                        ) : (
                          user.cockpits.allowed.map(cockpitId => {
                            const cockpit = cockpits.find(c => c.id === cockpitId);
                            return cockpit ? (
                            <span key={cockpitId} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {cockpit.name}
                            </span>
                            ) : null;
                          })
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">Aucun cockpit</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                    <div className="flex flex-col gap-1">
                      {user.issuperadmin && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200 w-fit">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Super Admin
                        </span>
                      )}
                      {user.isadmin && !user.issuperadmin && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 w-fit">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Admin
                        </span>
                      )}
                      {!user.isadmin && !user.issuperadmin && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 w-fit">
                          Standard
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UsersManagement;
